import React from 'react';
import { 
  TableCell, 
  TableHead, 
  TableRow, 
  TableSortLabel,
  useTheme,
  alpha
} from '@mui/material';

// Define column keys for sorting
export type SortKey = 'id' | 'name' | 'username' | 'enabled' | 'updateDate' | 'roles';

// Table Header Component
const UsersTableHeader: React.FC<{
  sortBy: SortKey;
  sortDirection: 'asc' | 'desc';
  handleSort: (key: SortKey) => void;
}> = ({ sortBy, sortDirection, handleSort }) => {
  const theme = useTheme();
  
  const headerCellStyle = {
    fontWeight: 600, 
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    py: 1.5,
    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
    backgroundColor: theme.palette.background.default,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04)
    }
  };
  
  return (
    <TableHead>
      <TableRow>
        <TableCell 
          sx={{ 
            ...headerCellStyle, 
            display: { xs: 'none', md: 'table-cell' }
          }}
        >
          <TableSortLabel
            active={sortBy === 'id'}
            direction={sortBy === 'id' ? sortDirection : 'asc'}
            onClick={() => handleSort('id')}
            sx={{ 
              '& .MuiTableSortLabel-icon': {
                color: sortBy === 'id' ? `${theme.palette.primary.main} !important` : undefined
              }
            }}
          >
            ID
          </TableSortLabel>
        </TableCell>
        <TableCell sx={headerCellStyle}>
          <TableSortLabel
            active={sortBy === 'name'}
            direction={sortBy === 'name' ? sortDirection : 'asc'}
            onClick={() => handleSort('name')}
            sx={{
              '& .MuiTableSortLabel-icon': {
                color: sortBy === 'name' ? `${theme.palette.primary.main} !important` : undefined
              }
            }}
          >
            Name
          </TableSortLabel>
        </TableCell>
        <TableCell sx={headerCellStyle}>
          <TableSortLabel
            active={sortBy === 'username'}
            direction={sortBy === 'username' ? sortDirection : 'asc'}
            onClick={() => handleSort('username')}
            sx={{
              '& .MuiTableSortLabel-icon': {
                color: sortBy === 'username' ? `${theme.palette.primary.main} !important` : undefined
              }
            }}
          >
            Email
          </TableSortLabel>
        </TableCell>
        <TableCell sx={headerCellStyle}>
          <TableSortLabel
            active={sortBy === 'updateDate'}
            direction={sortBy === 'updateDate' ? sortDirection : 'asc'}
            onClick={() => handleSort('updateDate')}
            sx={{
              '& .MuiTableSortLabel-icon': {
                color: sortBy === 'updateDate' ? `${theme.palette.primary.main} !important` : undefined
              }
            }}
          >
            Last Updated
          </TableSortLabel>
        </TableCell>
        <TableCell align="center" sx={headerCellStyle}>
          <TableSortLabel
            active={sortBy === 'enabled'}
            direction={sortBy === 'enabled' ? sortDirection : 'asc'}
            onClick={() => handleSort('enabled')}
            sx={{
              '& .MuiTableSortLabel-icon': {
                color: sortBy === 'enabled' ? `${theme.palette.primary.main} !important` : undefined
              }
            }}
          >
            Status
          </TableSortLabel>
        </TableCell>
        <TableCell sx={headerCellStyle}>
          <TableSortLabel
            active={sortBy === 'roles'}
            direction={sortBy === 'roles' ? sortDirection : 'asc'}
            onClick={() => handleSort('roles')}
            sx={{
              '& .MuiTableSortLabel-icon': {
                color: sortBy === 'roles' ? `${theme.palette.primary.main} !important` : undefined
              }
            }}
          >
            Roles
          </TableSortLabel>
        </TableCell>
        <TableCell align="center" sx={headerCellStyle}>Actions</TableCell>
      </TableRow>
    </TableHead>
  );
};

export default UsersTableHeader;